import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Check, Plus } from "lucide-react";
import type { CollectionEntryKind } from "@connect/shared";
import { useCollections, useEntryCollections } from "@/lib/useCollections";
import { useTrack } from "@/lib/track";
import { cn } from "@/lib/cn";

interface Props {
  kind: CollectionEntryKind;
  entryId: string;
  /** Compact label option for tight surfaces (cards). Default is icon-only. */
  variant?: "icon" | "labeled";
}

/**
 * Quiet collection picker. Typography-first dropdown — no folder icons, no
 * heavy form. Lists the user's collections with a ✓ next to those that
 * already contain this entry; clicking toggles membership. Bottom of the
 * list has a "New collection" inline input.
 */
export function AddToCollectionMenu({ kind, entryId, variant = "icon" }: Props) {
  const collections = useCollections();
  const { lookup, add, remove, createAndAdd } = useEntryCollections(kind, entryId);
  const track = useTrack();
  const [newName, setNewName] = useState("");

  const memberships = lookup.data?.memberships ?? [];
  const memberMap = new Map(memberships.map((m) => [m.id, m.entry_id]));

  const handleToggle = (collectionId: string) => {
    const existingEntryId = memberMap.get(collectionId);
    if (existingEntryId) {
      remove.mutate({ collectionId, entryId: existingEntryId });
    } else {
      add.mutate(collectionId);
      track("collection.entry.add", { kind, source: "menu" });
    }
  };

  const handleCreate = () => {
    const name = newName.trim();
    if (!name || createAndAdd.isPending) return;
    createAndAdd.mutate(name, {
      onSuccess: () => {
        setNewName("");
        track("collection.create", { source: "add_menu" });
        track("collection.entry.add", { kind, source: "new_collection" });
      },
    });
  };

  const inCount = memberships.length;

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          title={inCount > 0 ? `In ${inCount} collection${inCount === 1 ? "" : "s"}` : "Add to collection"}
          aria-label="Add to collection"
          className={cn(
            "p-1.5 rounded-md transition-colors hover:bg-veil/40",
            inCount > 0 ? "text-gold-glow" : "text-whisper hover:text-fog",
          )}
        >
          {variant === "labeled" ? (
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
              <Plus className="size-3" strokeWidth={1.5} />
              {inCount > 0 ? `In ${inCount}` : "Collect"}
            </span>
          ) : (
            <Plus className="size-4" strokeWidth={1.5} />
          )}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="z-50 min-w-[240px] max-w-[300px] panel p-1.5 border border-veil/60 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.6)]"
        >
          <div className="px-3 pt-2 pb-1.5 label-eyebrow text-muted">Collections</div>
          {collections.isLoading || lookup.isLoading ? (
            <div className="px-3 py-2 text-xs text-whisper">Reading…</div>
          ) : (collections.data?.collections.length ?? 0) === 0 ? (
            <div className="px-3 py-2 text-xs text-whisper italic">No collections yet.</div>
          ) : (
            <div className="max-h-[280px] overflow-y-auto">
              {collections.data!.collections.map((col) => {
                const isIn = memberMap.has(col.id);
                return (
                  <DropdownMenu.Item
                    key={col.id}
                    onSelect={(e) => {
                      e.preventDefault();
                      handleToggle(col.id);
                    }}
                    className={cn(
                      "px-3 py-2 text-sm rounded cursor-pointer outline-none flex items-center justify-between gap-3",
                      "text-fog hover:bg-veil/40 data-[highlighted]:bg-veil/40",
                    )}
                  >
                    <span className="truncate">{col.name}</span>
                    {isIn && <Check className="size-3.5 text-gold-glow shrink-0" strokeWidth={2} />}
                  </DropdownMenu.Item>
                );
              })}
            </div>
          )}
          <DropdownMenu.Separator className="my-1.5 h-px bg-veil/40" />
          <div className="px-2 py-1.5">
            <p className="px-1 mb-1.5 label-eyebrow text-muted">New collection</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                placeholder="Name it"
                className="flex-1 bg-ink-elev/60 border border-veil/50 rounded px-2 py-1.5 text-xs text-fog placeholder:text-muted focus:outline-none focus:border-gold/40"
              />
              <button
                type="button"
                onClick={handleCreate}
                disabled={!newName.trim() || createAndAdd.isPending}
                className="text-[10px] uppercase tracking-[0.18em] text-whisper hover:text-fog disabled:opacity-40"
              >
                {createAndAdd.isPending ? "…" : "Add"}
              </button>
            </div>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
